/**
 * In-flight voice call banner (013 Phase 6 / US4).
 *
 * Renders when CrmLockState.inFlightCallId is non-null. Two variants:
 *   - 'expanded' for the contact drawer + Settings panel; shows contact
 *     name, provider, status, live elapsed time, Cancel button.
 *   - 'compact' for the global TopBar; shows a small pulse dot + label
 *     that's clickable to navigate to the in-flight contact.
 *
 * Polling on /crm/state (3-5s) is owned by the parent — this component is
 * a pure renderer over the lockState prop.
 */
import type { CrmLockState } from '@peerless/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PhoneCall, PhoneOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { ApiError, apiRequest } from '../../../lib/api-client';
import { queryKeys } from '../../../lib/query-keys';


interface ExpandedProps {
  variant: 'expanded';
  lockState: CrmLockState;
}

interface CompactProps {
  variant: 'compact';
  lockState: CrmLockState;
}

type Props = ExpandedProps | CompactProps;

function formatElapsed(startedAtIso: string | null): string {
  if (!startedAtIso) return '0:00';
  const startedAt = new Date(startedAtIso).getTime();
  const seconds = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function InFlightCallBanner(props: Props): JSX.Element | null {
  const qc = useQueryClient();
  const lock = props.lockState;
  const [elapsed, setElapsed] = useState(() => formatElapsed(lock.inFlightStartedAt));

  // Live tick — 1s while the banner is mounted.
  useEffect(() => {
    if (!lock.inFlightCallId) return;
    const handle = setInterval(() => {
      setElapsed(formatElapsed(lock.inFlightStartedAt));
    }, 1000);
    return () => clearInterval(handle);
  }, [lock.inFlightCallId, lock.inFlightStartedAt]);

  const cancelMutation = useMutation({
    mutationFn: (callLogId: string) =>
      apiRequest<{ cancelled: string }>(`/voice-agent/calls/${callLogId}/cancel`, {
        method: 'POST',
      }),
    onSuccess: () => {
      toast.success('Call cancelled');
      void qc.invalidateQueries({ queryKey: queryKeys.crm.state() });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Cancel failed'),
  });

  if (!lock.inFlightCallId) return null;

  if (props.variant === 'compact') {
    return (
      <Link
        to={lock.inFlightContactId ? `/crm?contact=${lock.inFlightContactId}` : '/crm'}
        className="flex items-center gap-2 rounded-full bg-caution/15 px-3 py-1 text-xs font-medium hover:bg-caution/25"
        title={`Voice agent in call with ${lock.inFlightContactName ?? 'unknown contact'}`}
      >
        <span className="relative inline-flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-500 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-600" />
        </span>
        <span>Voice agent in call</span>
        <span className="font-mono">{elapsed}</span>
      </Link>
    );
  }

  return (
    <div className="flex items-center justify-between gap-3 rounded border border-amber-400/60 bg-amber-50/50 dark:bg-amber-950/20 p-3">
      <div className="flex items-center gap-3 min-w-0">
        <PhoneCall className="h-5 w-5 text-amber-600 flex-shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-semibold">
            Voice agent in call with{' '}
            <span className="text-amber-700 dark:text-amber-300">
              {lock.inFlightContactName ?? 'unknown contact'}
            </span>
          </div>
          <div className="text-xs text-muted-foreground">
            Provider: {lock.inFlightProvider ?? 'unknown'} · Status:{' '}
            {lock.inFlightStatus ?? 'in progress'} · Elapsed:{' '}
            <span className="font-mono">{elapsed}</span>
          </div>
        </div>
      </div>
      <button
        type="button"
        className="rounded border border-destructive/50 px-3 py-1.5 text-xs text-destructive hover:bg-destructive/10 disabled:opacity-50 flex items-center gap-1"
        onClick={() => cancelMutation.mutate(lock.inFlightCallId!)}
        disabled={cancelMutation.isPending}
      >
        <PhoneOff className="h-3 w-3" /> Cancel
      </button>
    </div>
  );
}
